<template>
  <div class="dynamic-form" :style="cssProps">
    <div
      v-for="(field, index) in formData"
      :key="index"
      class="form-element"
      :class="{ 'checkbox-wrapper': field.type == 'Checkbox' }"
    >
      <div v-if="showIfTrue(field, true)">
        <div v-if="supportedTypes.includes(field.type)"></div>
        <!-- Dropdown -->
        <div
          v-if="field.type == 'Dropdown'"
          class="select-dropdown form-item"
          :class="{ invalid: isInvalid(field.id) }"
        >
          <label :for="field.id">{{ field.name }}</label>
          <select
            :required="field.mandatory"
            :id="field.id + '-' + formDataIndex"
            :placeholder="field.name"
            v-model="source[field.id]"
          >
            <!-- { "name": "products", "value": "products", "label": "Product data" }, -->
            <option v-for="(option, index) in field.options" :key="index" :value="option.value">{{
              option.label
            }}</option>
          </select>
        </div>

        <!-- checkbox -->
        <div v-if="field.type == 'Checkbox'" class="checkbox form-item">
          <checkbox lightLabel :label="field.name || field.label" :value="field.id" v-model="source[field.id]" />
        </div>
        <!-- checkbox -->
        <div v-if="field.type == 'CheckboxGroup'" class="checkbox-group form-item">
          <checkbox-group
            :lightLabel="true"
            :listClasses="'checkbox-group'"
            :label="field.name || field.label"
            :options="field.options"
            :groupId="formDataIndex"
            :source="source"
          />
        </div>

        <!-- radio group -->
        <div v-if="field.type == 'Radio'" class="radio-group form-item">
          <radio-button
            lightLabel
            :listClasses="'radio-btns-dynamic'"
            :label="field.name"
            :options="field.options"
            :groupId="formDataIndex"
            v-model="source[field.id]"
          />
        </div>

        <!-- multiselect -->
        <div v-if="field.type == 'Multiselect'" class="form-item">
          <multi-select
            class="dynamic-multiselect"
            :label="field.name"
            :placeholder="field.placeholder || field.description || ' '"
            :options="field.options"
            :name="formDataIndex"
            :value="source[field.id]"
            :required="field.mandatory"
            :invalidInput="isInvalid(field.id)"
            @value-change="(result) => handleSetMultiselectValue(result, field.id)"
          />
        </div>

        <!-- text -->
        <div v-if="textTypes.includes(field.type)" class="text form-item">
          <custom-input
            :invalidInput="field.mandatory ? isInvalid(field.id) : false"
            :isRequired="field.mandatory"
            :isReadonly="field.readonly"
            :inputType="field.type"
            :toggleVisibility="field.secret"
            :name="field.id + '-' + formDataIndex"
            :label="field.name"
            :placeholder="field.placeholder || field.description || ' '"
            @option-btn-clicked="field.applySearchButton ? $emit('search-example-id') : null"
            :optionBtn="field.applySearchButton"
            :btnVisible="field.applySearchButton"
            :btnText="field.applySearchButton ? 'Find Id' : null"
            :focusInputField="field.focus"
            v-model="source[field.id]"
            :boxed="boxed"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomInput from "@/components/CustomInput";
import Checkbox from "@/components/Checkbox";
import CheckboxGroup from "@/components/CheckboxGroup";
import RadioButton from "@/components/RadioButton";
import MultiSelect from "@/components/MultiSelect.vue";
export default {
  name: "dynamicForm",
  props: {
    formData: Array,
    formDataIndex: { type: Number, default: 0 },
    source: Object,
    ignoreInvalid: {
      type: Array, 
      default: () => []
    },
    boxed: Boolean
  },
  data() {
    return {
      textTypes: ["Text", "Password", "Number"],
      supportedTypes: ["Text", "Password", "Dropdown", "Checkbox", "Radio"],
      formValues: null,
      invalidFields: [],
      invalidForm: false,
      invalidFormMsg: ""
    };
  },
  computed: {
    formLength() {
      return this.formData ? this.formData.length : 0;
    },
    cssProps() {
      return { "--form-length": Math.round(this.formLength / 2) };
    }
  },
  methods: {
    isInvalid(id) {
      if (this.ignoreInvalid.includes(id)) return false;
      else return this.invalidFields.map((f) => f.id).includes(id);
    },

    showIfTrue(field, setDefault) {
      if (!field.showIfTrue) return true;

      const val = field.showIfTrue;
      const fieldAndValue = val.split("=");
      const returnVal =
        val && val.includes("=")
          ? this.source[fieldAndValue[0]] == fieldAndValue[1]
          : this.source[fieldAndValue[0]] === "true" || this.source[fieldAndValue[0]] === true;

      if (!returnVal && setDefault) this.$set(this.source, field.id, field.default);
      return returnVal;
    },

    handleSetMultiselectValue(result, id) {
      this.$set(this.source, id, result);
    },

    checkMandatoryFields() {
      this.invalidFields = [];
      const mandatoryFields = this.formData.filter((d) => d.mandatory == true);
      mandatoryFields.forEach((d) => {
        if (this.showIfTrue(d) && (!this.source[d.id] || this.source[d.id].length < 1)) {
          this.invalidFields.push(d);
        }
      });

      if (this.invalidFields.length > 0) {
        this.invalidForm = true;
        const names = this.invalidFields.map((f) => f.name);
        const joined = this.invalidFields.length == 1 ? names : names.slice(0, -1).join(", ") + " & " + names.slice(-1);
        this.invalidFormMsg = `${joined} ${
          this.invalidFields.length == 1
            ? this.$t("dynamic-form.invalid-msg-singular")
            : this.$t("dynamic-form.invalid-msg-plural")
        }`;
      } else this.invalidForm = false;
     
      const emptyFields = [];
      for (const key in this.source) {
        if (this.source[key].length == 0) emptyFields.push(key);
      }
      
      this.$emit("invalid-fields", {
        invalidForm: this.invalidForm,
        invalidFormMsg: this.invalidForm ? this.invalidFormMsg : '',
        emptyFields: emptyFields
      });
    }
  },
  watch: {
    source: {
      handler: function() {
        this.$emit("changes", this.formDataIndex);
        this.checkMandatoryFields();
      },
      deep: true
    }
  },
  mounted() {
    this.checkMandatoryFields();
    this.$nextTick(() => {
      this.$emit("form-mounted");
    });
  },
  components: {
    CustomInput,
    Checkbox,
    RadioButton,
    CheckboxGroup,
    MultiSelect
  }
};
</script>

<style lang="less">
.dynamic-form {
  .radio-btns {
    display: flex;
    align-items: center;
    gap: 12px;

    .checkbox {
      grid-row: 4;
    }
    .radio-container {
      display: flex;
      align-items: center;
    }
  }
  .form-element.checkbox-wrapper {
    display: flex;
    align-items: center;
    .container-checkbox {
      margin-top: 14px;
      margin-bottom: 0;
    }
  }

  .radio-btns-dynamic {
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 18px;
    .radio-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 0;
    }
  }
  .radio-btns-wrapper {
    padding: 10px 0 0 0;
  }

  .dynamic-multiselect {
    margin-top: 30px;
  }
}
</style>
